import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import ModalOperations, { OperationData } from '../modal/modalAddOperation';
import { showError, showSuccess } from '../notifications';
import { useModalAddOperation } from '../hooks/useModalAddOperation';
import { Link } from 'react-router-dom';
import { Card, CardContent, ListItemButton, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatCurrency } from '../../utils/formatMoney';

interface ListItemsProps {
  type: 'all' | 'income' | 'expense' | 'category';
  items: OperationData[];
  page?: 'home' | null;
}

type OpsListsCtx = {
  prevAll?: OperationData[];
  prevIncome?: OperationData[];
  prevExpense?: OperationData[];
};

export default function ListItems({ type, items, page }: ListItemsProps) {
  const { openAddOperation, handleOpenAddOperation, handleCloseAddOperation } =
    useModalAddOperation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<OperationData | null>(null);
  const [editingItem, setEditingItem] = useState<OperationData | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [title, setTitle] = useState<string>('');

  const queryClient = useQueryClient();

  useEffect(() => {
    if (type === 'income') {
      setTitle('Доходы');
    }

    if (type === 'expense') {
      setTitle('Расходы');
    }
  }, []);

  const deleteMutation = useMutation<void, Error, OperationData, OpsListsCtx>({
    mutationFn: op => {
      const base =
        op.type === 'income'
          ? '/incomes'
          : op.type === 'expense'
            ? '/expenses'
            : (op as any).categoryincomeId != null
              ? '/incomes'
              : '/expenses';

      return api.delete(`${base}/${op.id}`).then(() => {});
    },

    async onMutate(op) {
      await queryClient.cancelQueries({ queryKey: ['all'] });
      await queryClient.cancelQueries({ queryKey: ['incomes'] });
      await queryClient.cancelQueries({ queryKey: ['expenses'] });

      const prevAll = queryClient.getQueryData<OperationData[]>(['all']);
      const prevIncome = queryClient.getQueryData<OperationData[]>(['incomes']);
      const prevExpense = queryClient.getQueryData<OperationData[]>(['expenses']);

      if (prevAll) {
        queryClient.setQueryData(
          ['all'],
          prevAll.filter(x => x.id !== op.id),
        );
      }
      if (op.type === 'income' && prevIncome) {
        queryClient.setQueryData(
          ['incomes'],
          prevIncome.filter(x => x.id !== op.id),
        );
      }
      if (op.type === 'expense' && prevExpense) {
        queryClient.setQueryData(
          ['expenses'],
          prevExpense.filter(x => x.id !== op.id),
        );
      }

      return { prevAll, prevIncome, prevExpense };
    },

    onError(err, _op, ctx) {
      if (ctx?.prevAll) queryClient.setQueryData(['all'], ctx.prevAll);
      if (ctx?.prevIncome) queryClient.setQueryData(['incomes'], ctx.prevIncome);
      if (ctx?.prevExpense) queryClient.setQueryData(['expenses'], ctx.prevExpense);
      showError(err.message);
    },

    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['all'] });
      queryClient.invalidateQueries({ queryKey: ['income'] });
      queryClient.invalidateQueries({ queryKey: ['expense'] });
      showSuccess('Операция удалена');
    },

    onSettled() {
      queryClient.invalidateQueries({ queryKey: ['all'] });
      queryClient.invalidateQueries({ queryKey: ['income'] });
      queryClient.invalidateQueries({ queryKey: ['expense'] });
    },
  });
  const handleDeleteClick = (op: OperationData) => {
    setToDelete(op);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (toDelete) deleteMutation.mutate(toDelete);
    setConfirmOpen(false);
    setToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setToDelete(null);
  };

  const handleEditClick = (op: OperationData) => {
    setModalMode('edit');
    setEditingItem(op);
    handleOpenAddOperation();
  };

  const handleCopyClick = (op: OperationData) => {
    setModalMode('copy');
    setEditingItem(op);
    handleOpenAddOperation();
  };

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
      <Card>
        <CardContent>
          {title && page == 'home' && (
            <Typography ml={2} sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
              {title}
            </Typography>
          )}
          <List>
            {items.length > 0 ? (
              items.map(item => (
                <React.Fragment key={item.id}>
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <>
                        <IconButton edge="end" onClick={() => handleEditClick(item)} sx={{ mr: 1 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={() => handleCopyClick(item)} sx={{ mr: 1 }}>
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={() => handleDeleteClick(item)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemText
                      primary={`${formatCurrency(item.amount)} — ${item.name}`}
                      secondary={
                        <>
                          <div>{new Date(item.date).toLocaleString('ru-RU')}</div>
                          {item.description && <div>Описание: {item.description}</div>}

                          {(item.categoryincome || item.categoryexpense) && (
                            <div>
                              Категория:{' '}
                              <Link
                                to={`/operationscategory/${item.categoryincome?.id || item.categoryexpense?.id}?type=${item.categoryincome ? 'income' : 'expense'}`}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                              >
                                {item.categoryincome?.name ||
                                  item.categoryexpense?.name ||
                                  'Без категории'}
                              </Link>
                            </div>
                          )}

                          {Array.isArray(item.tagincomes) && item.tagincomes.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {item.tagincomes.map(tag => (
                                <Link to={`/operationstag/${tag.id}?type=income`}>
                                  <span
                                    key={tag.id}
                                    style={{
                                      display: 'inline-block',
                                      backgroundColor: '#e0f7fa',
                                      borderRadius: 4,
                                      padding: '2px 6px',
                                      marginRight: 4,
                                      fontSize: 12,
                                    }}
                                  >
                                    {tag.name}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}

                          {Array.isArray(item.tagexpenses) && item.tagexpenses.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {item.tagexpenses.map(tag => (
                                <Link to={`/operationstag/${tag.id}?type=expense`}>
                                  <span
                                    key={tag.id}
                                    style={{
                                      display: 'inline-block',
                                      backgroundColor: '#ffebee',
                                      borderRadius: 4,
                                      padding: '2px 6px',
                                      marginRight: 4,
                                      fontSize: 12,
                                    }}
                                  >
                                    {tag.name}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="Нет записей"
                  primaryTypographyProps={{ color: 'text.secondary' }}
                />
              </ListItem>
            )}
            {page == 'home' && (
              <>
                <Divider component="li" />
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/operations"
                    sx={{ justifyContent: 'center' }}
                  >
                    Посмотреть все операции
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
          <Dialog open={confirmOpen} onClose={handleCancelDelete}>
            <DialogTitle>Удалить запись?</DialogTitle>
            <DialogActions>
              <Button onClick={handleCancelDelete}>Отмена</Button>
              <Button color="error" onClick={handleConfirmDelete}>
                Удалить
              </Button>
            </DialogActions>
          </Dialog>
          <ModalOperations
            mode={modalMode}
            typeItem={
              editingItem?.type ?? (type === 'income' || type === 'expense' ? type : undefined)
            }
            open={openAddOperation}
            initialData={editingItem ?? undefined}
            onClose={() => {
              handleCloseAddOperation();
              setModalMode('create');
              setEditingItem(null);
            }}
            onSubmit={() => {
              handleCloseAddOperation();
              setModalMode('create');
              setEditingItem(null);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
